import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import {
    setUpdatePhoneModalOpen,
    setUserVerificationHelpModalOpen,
} from '@perpay-web/components/actions/userVerificationModal';
import UpdatePhoneNumberModal from '@perpay-web/components/composite/UpdatePhoneNumberModal/UpdatePhoneNumberModal';
import UserVerificationHelpModal from '@perpay-web/components/composite/UserVerificationHelpModal/UserVerificationHelpModal';
import UserVerificationModal from '@perpay-web/components/composite/UserVerificationModal/UserVerificationModal';
import { useSignupRequirementsDataContext } from '@perpay-web/components/hooks/useSignupRequirementsDataContext';
import { getErrors, getIsLoading } from '@perpay-web/data-module/useDataModule';
import {
    analyticsPartnerHostedOnboardingVerificationModalHelpViewed,
    analyticsPartnerHostedOnboardingVerificationModalPasswordSubmitClicked,
    analyticsPartnerHostedOnboardingVerificationModalPasswordViewed,
    analyticsPartnerHostedOnboardingVerificationModalUpdatePhoneViewed,
    analyticsPartnerHostedOnboardingVerificationModalVerifyPhoneViewed,
} from '@perpay-web/fintech/actions/analytics/partnerHostedOnboarding';
import { setPhone, verifyPhone } from '@perpay-web/fintech/actions/ui/signup';
import {
    fetchUserInfoDataModule,
    getPhoneNumber,
} from '@perpay-web/fintech/dataModules/fetchUserInfo';
import { createPasswordDataModule } from '@perpay-web/fintech/dataModules/partnerOnboardingCreatePassword';
import { useUpdateUserInfoContext } from '@perpay-web/fintech/hooks/dataModule/useUpdateUserInfoContext';
import { useUpdatePhoneNumberModalContext } from '@perpay-web/fintech/hooks/useUpdatePhoneNumberModalContext';
import { useUserVerificationHelpModalContext } from '@perpay-web/fintech/hooks/useUserVerificationHelpModalContext';
import { getPartnerCode } from '@perpay-web/fintech/selectors/ui/partnerOnboarding';
import {
    getPhoneVerificationJustCompleted,
    getSignupErrors,
    getSignupLoading,
} from '@perpay-web/fintech/selectors/ui/signup';
import { launchSupportTool as launchSupportToolAction } from '@perpay-web/services/supportTool';

/**
 * @param {{
 *   className?: string,
 *   onClose?: () => void,
 * }} props
 */
const UserVerificationModalContainer = ({ className, onClose }) => {
    const dispatch = useDispatch();
    const location = useLocation();

    const partnerCode = useSelector(getPartnerCode);

    // Get signup requirements from context - will update when data refetches
    const { data: signupRequirementsData } = useSignupRequirementsDataContext();

    const passwordLoading = useSelector(createPasswordDataModule.getIsLoading);
    const passwordErrors = useSelector(createPasswordDataModule.getErrors);

    const phone = useSelector(getPhoneNumber);
    const phoneErrors = useSelector(getSignupErrors);
    const phoneLoading = useSelector(getSignupLoading);
    const phoneVerificationJustCompleted = useSelector(
        getPhoneVerificationJustCompleted,
    );

    const {
        isAnimatedModalOpen: isUserVerificationHelpModalOpen,
        setIsAnimatedModalOpen: setIsUserVerificationHelpModalOpen,
        animatedModalClassName: userVerificationHelpModalClassName,
    } = useUserVerificationHelpModalContext();

    const {
        isAnimatedModalOpen: isUpdatePhoneModalOpen,
        setIsAnimatedModalOpen: setIsUpdatePhoneModalOpen,
        animatedModalClassName: updatePhoneModalClassName,
    } = useUpdatePhoneNumberModalContext();

    const {
        state: updateUserInfoState,
        dataRequest: updateUserInfoRequest,
        dataReset: updateUserInfoReset,
    } = useUpdateUserInfoContext();
    const isUpdateUserInfoLoading = getIsLoading(updateUserInfoState);
    const updateUserInfoErrors = getErrors(updateUserInfoState);
    const isUserInfoLoading = useSelector(
        fetchUserInfoDataModule.getIsLoadingOrUnrequested,
    );

    const onMount = useCallback(() => {
        dispatch(fetchUserInfoDataModule.dataRequest());
    }, [dispatch]);

    const onPasswordViewed = useCallback(() => {
        dispatch(
            analyticsPartnerHostedOnboardingVerificationModalPasswordViewed({
                partner: partnerCode,
                path: location.pathname,
                entryPoint: 'create-password-modal',
            }),
        );
    }, [dispatch, partnerCode, location.pathname]);

    const onSubmitPassword = useCallback(
        (values) => {
            dispatch(
                analyticsPartnerHostedOnboardingVerificationModalPasswordSubmitClicked(
                    {
                        partner: partnerCode,
                        path: location.pathname,
                        entryPoint: 'create-password-modal',
                    },
                ),
            );
            dispatch(
                createPasswordDataModule.dataRequest({
                    password: values,
                    dashboardRedirect: false,
                }),
            );
        },
        [dispatch, partnerCode, location.pathname],
    );

    const onVerifyPhoneNumber = useCallback(
        (values) => {
            // Don't set flag here - let the success/error handlers manage it
            dispatch(verifyPhone(values));
        },
        [dispatch],
    );

    const onResend = useCallback(() => {
        // Prevent resend if verification was just completed to avoid duplicate POST requests
        if (phoneVerificationJustCompleted) {
            return;
        }

        dispatch(
            setPhone({
                mobilePhone: phone,
            }),
        );
    }, [dispatch, phone, phoneVerificationJustCompleted]);

    const launchSupportTool = useCallback(() => {
        dispatch(launchSupportToolAction());
    }, [dispatch]);

    const onLoadPhoneVerificationView = useCallback(() => {
        dispatch(
            analyticsPartnerHostedOnboardingVerificationModalVerifyPhoneViewed({
                partner: partnerCode,
                path: location.pathname,
            }),
        );
    }, [dispatch, partnerCode, location.pathname]);

    const handleClose = useCallback(() => {
        if (onClose) {
            onClose();
        }
    }, [onClose]);

    const onHelpButtonClick = useCallback(
        () => dispatch(setUserVerificationHelpModalOpen(true)),
        [dispatch],
    );

    const onEditPhoneNumber = useCallback(
        () => dispatch(setUpdatePhoneModalOpen(true)),
        [dispatch],
    );

    const updatePhoneNumber = useCallback(
        (mobilePhone) =>
            dispatch(
                updateUserInfoRequest({
                    mobilePhone: `+1${mobilePhone}`,
                }),
            ),
        [dispatch, updateUserInfoRequest],
    );

    const onClearSetPhone = useCallback(
        () => updateUserInfoReset(),
        [updateUserInfoReset],
    );

    const onLoadHelpView = useCallback(
        () =>
            dispatch(
                analyticsPartnerHostedOnboardingVerificationModalHelpViewed({
                    partner: partnerCode,
                }),
            ),
        [dispatch, partnerCode],
    );

    const onLoadUpdatePhoneView = useCallback(
        () =>
            dispatch(
                analyticsPartnerHostedOnboardingVerificationModalUpdatePhoneViewed(
                    {
                        partner: partnerCode,
                    },
                ),
            ),
        [dispatch, partnerCode],
    );

    if (isUpdatePhoneModalOpen) {
        return (
            <UpdatePhoneNumberModal
                className={updatePhoneModalClassName}
                loading={isUpdateUserInfoLoading}
                errors={updateUserInfoErrors}
                onClose={() => setIsUpdatePhoneModalOpen(false)}
                launchSupportTool={launchSupportTool}
                updatePhoneNumber={updatePhoneNumber}
                clearBackendErrors={onClearSetPhone}
                onLoadPageView={onLoadUpdatePhoneView}
            />
        );
    }

    if (isUserVerificationHelpModalOpen) {
        return (
            <UserVerificationHelpModal
                className={userVerificationHelpModalClassName}
                onClose={() => setIsUserVerificationHelpModalOpen(false)}
                launchSupportTool={launchSupportTool}
                onEditPhoneNumber={onEditPhoneNumber}
                onLoadPageView={onLoadHelpView}
            />
        );
    }

    return (
        <UserVerificationModal
            className={className}
            onClose={handleClose}
            signupRequirements={signupRequirementsData}
            passwordLoading={passwordLoading}
            passwordErrors={passwordErrors}
            onPasswordViewed={onPasswordViewed}
            onSubmitPassword={onSubmitPassword}
            onVerifyPhoneNumber={onVerifyPhoneNumber}
            mobilePhone={phone}
            onResend={onResend}
            phoneLoading={phoneLoading}
            phoneErrors={phoneErrors}
            onLoadPhoneVerificationView={onLoadPhoneVerificationView}
            onHelpButtonClick={onHelpButtonClick}
            onMount={onMount}
            isUserInfoLoading={isUserInfoLoading}
            launchSupportTool={launchSupportTool}
            openPhoneNumberModal={onEditPhoneNumber}
        />
    );
};

export default UserVerificationModalContainer;
